import React, { useState, useEffect, useRef } from 'react';
import { format, parseISO, addHours, isPast } from 'date-fns';
import { Loader2, XCircle, Video, ExternalLink, Copy, X, Play } from 'lucide-react';
import { toast } from 'sonner';
import { useVideoRingNotificationsContext } from '@/contexts/VideoRingNotificationsContext';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { CalendarEvent, CalendarEventInsert, CalendarEventUpdate } from '@/hooks/useCalendarEvents';
import { useCalendarEventMutations } from '@/hooks/useCalendarEvents';
import { useActiveAppUsersForSelect } from '@/hooks/useAppUsers';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { supabase as _supabase } from '@/integrations/supabase/client';
import { ensureMeetingForEvent } from '@/lib/meetings/ensureMeetingForEvent';

// calendar_events not yet in generated types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const supabase = _supabase as any;

interface EventFormModalProps {
  open: boolean;
  onClose: () => void;
  /** If provided, opens in edit mode */
  event?: CalendarEvent | null;
  /** Pre-fill start date when creating from day click */
  defaultDate?: Date | null;
  /** When true, all form fields are disabled and save/delete hidden (view-only mode) */
  readOnly?: boolean;
  /** Pre-set event type and hide the Tipo selector (e.g. when coming from TaskFormModal Reunião Video) */
  defaultType?: 'meeting' | 'reminder' | 'other' | 'unavailable';
}

function toLocalDatetimeValue(iso: string) {
  const d = parseISO(iso);
  return format(d, "yyyy-MM-dd'T'HH:mm");
}

function localInputToISO(value: string) {
  return new Date(value).toISOString();
}

interface SearchItem {
  id: string;
  name: string;
  sub?: string | null;
}

export function EventFormModal({ open, onClose, event, defaultDate, readOnly: readOnlyProp, defaultType }: EventFormModalProps) {
  const isEditing = !!event;
  const isCanceled = event?.status === 'canceled';
  const isPastDailyMeeting = isEditing && !!event?.daily_room_url && isPast(parseISO(event.end_at));
  const isReadOnly = readOnlyProp || isPastDailyMeeting || isCanceled;
  const { createEvent, updateEvent, deleteEvent } = useCalendarEventMutations();
  const { users } = useActiveAppUsersForSelect();
  const { data: currentUser } = useCurrentAppUser();
  const { openDailyCall } = useVideoRingNotificationsContext();

  const defaultStart = defaultDate ?? new Date();
  const defaultEnd = addHours(defaultStart, 1);

  const [title, setTitle] = useState('');
  const [type, setType] = useState<'meeting' | 'reminder' | 'other' | 'unavailable'>('meeting');
  const [allDay, setAllDay] = useState(false);
  const [startAt, setStartAt] = useState(format(defaultStart, "yyyy-MM-dd'T'HH:mm"));
  const [endAt, setEndAt] = useState(format(defaultEnd, "yyyy-MM-dd'T'HH:mm"));
  const [description, setDescription] = useState('');
  const [attendeeIds, setAttendeeIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [joiningRoom, setJoiningRoom] = useState(false);
  const [copyingLink, setCopyingLink] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [dailyRecordingId, setDailyRecordingId] = useState<string | null>(null);

  // Contact search
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState<SearchItem[]>([]);
  const [contactSearching, setContactSearching] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [selectedContactName, setSelectedContactName] = useState('');
  const contactDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);


  // Auto-adjust endAt when startAt moves past it
  useEffect(() => {
    if (!allDay && startAt && endAt) {
      const s = new Date(startAt);
      const e = new Date(endAt);
      if (e <= s) {
        setEndAt(format(addHours(s, 1), "yyyy-MM-dd'T'HH:mm"));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAt]);

  // Debounced contact search
  useEffect(() => {
    if (contactDebounce.current) clearTimeout(contactDebounce.current);
    if (!contactSearch.trim() || contactSearch.length < 2) {
      setContactResults([]);
      return;
    }
    setContactSearching(true);
    contactDebounce.current = setTimeout(async () => {
      try {
        const { data } = await supabase.rpc('search_contacts_unaccent', {
          search_term: contactSearch.trim(),
          max_results: 8,
        });
        setContactResults(
          (data ?? []).map((c: any) => ({
            id: c.id,
            name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Contato',
            sub: c.primary_email,
          })),
        );
      } finally {
        setContactSearching(false);
      }
    }, 300);
  }, [contactSearch]);

  // Fetch contact name when editing an existing event
  useEffect(() => {
    if (!event) return;
    if (event.contact_id) {
      supabase
        .from('contacts')
        .select('id, first_name, last_name')
        .eq('id', event.contact_id)
        .single()
        .then(({ data }: any) => {
          if (data) {
            setSelectedContactId(data.id);
            setSelectedContactName(`${data.first_name || ''} ${data.last_name || ''}`.trim() || 'Contato');
          }
        });
    }
  }, [event?.id]);

  // Fetch recording URL via SECURITY DEFINER RPC (bypasses RLS)
  useEffect(() => {
    setRecordingUrl(null);
    setAudioUrl(null);
    setDailyRecordingId(null);
    const roomName = event?.daily_room_name;
    if (!roomName) return;
    const fetchRecording = async () => {
      try {
        const { data, error } = await supabase.rpc('get_meeting_recording_url', {
          p_room_name: roomName,
        });
        if (error) {
          console.error('[EventFormModal] RPC get_meeting_recording_url error:', error);
          return;
        }
        // RPC returns a table (array); take first row
        const row = Array.isArray(data) ? data[0] : data;
        if (row?.recording_url) {
          setRecordingUrl(row.recording_url);
        }
        if (row?.audio_url) {
          setAudioUrl(row.audio_url);
        }
        if (row?.daily_recording_id) {
          setDailyRecordingId(row.daily_recording_id);
        }
      } catch (err) {
        console.error('[EventFormModal] fetch recording failed:', err);
      }
    };
    fetchRecording();
    const interval = setInterval(fetchRecording, 15000);
    return () => clearInterval(interval);
  }, [event?.daily_room_name]);

  // Reset form when modal opens/closes or event changes
  useEffect(() => {
    if (!open) return;
    if (event) {
      setTitle(event.title);
      setType(event.type);
      setAllDay(event.all_day);
      setStartAt(toLocalDatetimeValue(event.start_at));
      setEndAt(toLocalDatetimeValue(event.end_at));
      setDescription(event.description ?? '');
      setAttendeeIds(event.attendee_ids ?? []);
      // contact/deal names loaded via separate effect above
      if (!event.contact_id) { setSelectedContactId(null); setSelectedContactName(''); }
    } else {
      const s = defaultDate ?? new Date();
      const e = addHours(s, 1);
      setTitle('');
      setType(defaultType ?? 'meeting');
      setAllDay(false);
      setStartAt(format(s, "yyyy-MM-dd'T'HH:mm"));
      setEndAt(format(e, "yyyy-MM-dd'T'HH:mm"));
      setDescription('');
      setAttendeeIds([]);
      setSelectedContactId(null);
      setSelectedContactName('');
      setContactSearch('');
      setContactResults([]);
    }
  }, [open, event, defaultDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    // Validate: don't allow scheduling in the past when editing
    const newStart = new Date(startAt);
    if (newStart < new Date()) {
      toast.error('Não é possível agendar para uma data/hora no passado');
      return;
    }

    // Validate: end must be after start
    const newEnd = new Date(endAt);
    if (!allDay && newEnd <= newStart) {
      toast.error('O horário de fim deve ser posterior ao horário de início');
      return;
    }

    setSubmitting(true);
    try {
      if (isEditing && event) {
        const newStartISO = localInputToISO(startAt);
        const newEndISO = localInputToISO(endAt);

        const update: CalendarEventUpdate = {
          title: title.trim(),
          type,
          all_day: allDay,
          start_at: newStartISO,
          end_at: newEndISO,
          description: description || null,
          attendee_ids: attendeeIds,
          contact_id: selectedContactId,
          deal_id: null,
        };
        await updateEvent.mutateAsync({ id: event.id, ...update });

        // Reverse sync: update matching call in `calls` table
        if (event.type === 'meeting' || event.daily_room_url) {
          try {
            const { data: matchingCall } = await supabase
              .from('calls')
              .select('id, google_calendar_event_id, handled_by_app_user_id')
              .eq('scheduled_for', event.start_at)
              .eq('contact_id', event.contact_id)
              .in('status', ['scheduled', 'pending'])
              .maybeSingle();

            if (matchingCall) {
              await supabase
                .from('calls')
                .update({
                  scheduled_for: newStartISO,
                  title: title.trim(),
                })
                .eq('id', matchingCall.id);

              console.log('[EventFormModal] synced call', matchingCall.id, 'to new time');
            }
          } catch (err) {
            console.warn('[EventFormModal] calls reverse sync failed:', err);
          }
        }
      } else {
        if (!currentUser) return;
        const insert: CalendarEventInsert = {
          title: title.trim(),
          type,
          all_day: allDay,
          start_at: localInputToISO(startAt),
          end_at: localInputToISO(endAt),
          description: description || null,
          attendee_ids: attendeeIds,
          status: 'scheduled',
          color: null,
          created_by: currentUser.id,
          contact_id: selectedContactId,
          deal_id: null,
          daily_room_url: null,
          daily_room_name: null,
        };
        await createEvent.mutateAsync(insert);

        // Mirror into calls so the event appears in contact/deal history
        if ((selectedContactId || event?.deal_id) && type === 'meeting') {
          supabase
            .from('calls')
            .insert({
              contact_id: selectedContactId,
              channel: 'video',
              status: 'scheduled',
              scheduled_for: localInputToISO(startAt),
              title: title.trim(),
              call_purpose: 'agenda_meeting',
              handled_by_app_user_id: currentUser.id,
              created_by: currentUser.id,
              direction: 'outbound',
            })
            .then(({ error }: any) => {
              if (error) console.warn('[EventFormModal] calls mirror failed:', error.message);
            });
        }
      }
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleCancelEvent = async () => {
    if (!event) return;
    setSubmitting(true);
    try {
      await updateEvent.mutateAsync({ id: event.id, status: 'canceled' });

      // Also cancel mirrored call record if exists
      try {
        const { data: matchingCall } = await supabase
          .from('calls')
          .select('id')
          .eq('channel', 'video')
          .eq('contact_id', event.contact_id)
          .eq('scheduled_for', event.start_at)
          .in('status', ['scheduled', 'pending'])
          .maybeSingle();

        if (matchingCall) {
          await supabase
            .from('calls')
            .update({ status: 'canceled', cancelled_at: new Date().toISOString() })
            .eq('id', matchingCall.id);
        }
      } catch (err) {
        console.warn('[EventFormModal] cancel mirror call failed:', err);
      }

      toast.success('Evento cancelado');
      setConfirmDeleteOpen(false);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAttendee = (userId: string) => {
    setAttendeeIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const getUserName = (u: { first_name: string | null; last_name: string | null; display_name: string | null }) =>
    u.display_name || [u.first_name, u.last_name].filter(Boolean).join(' ') || '—';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Evento' : 'Novo Evento'}</DialogTitle>
        </DialogHeader>

        {/* Join Meeting Button */}
        {isEditing && event?.type === 'meeting' && (
          <div className="flex items-center gap-2 p-3 rounded-lg border border-primary/20 bg-primary/5">
            <Video className="w-5 h-5 text-primary shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">
                {isPastDailyMeeting ? 'Reunião encerrada' : 'Videochamada'}
              </p>
            </div>
            <div className="flex gap-1.5">
              {!isPastDailyMeeting && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={copyingLink}
                  onClick={async () => {
                    setCopyingLink(true);
                    try {
                      const result = await ensureMeetingForEvent({
                        calendar_event_id: event.id,
                        daily_room_url: event.daily_room_url,
                        daily_room_name: event.daily_room_name,
                        contact_id: event.contact_id,
                        deal_id: event.deal_id,
                        end_at: event.end_at,
                        attendee_ids: event.attendee_ids,
                      });
                      navigator.clipboard.writeText(result.client_join_url);
                      toast.success(result.reused ? 'Link copiado!' : 'Sala criada e link copiado!');
                    } catch (err: any) {
                      console.error('Error copying link:', err);
                      toast.error('Erro ao copiar link: ' + (err.message || 'Tente novamente'));
                    } finally {
                      setCopyingLink(false);
                    }
                  }}
                >
                  {copyingLink ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <Copy className="w-3.5 h-3.5 mr-1" />
                  )}
                  {copyingLink ? 'Criando...' : 'Copiar Link'}
                </Button>
              )}
              {/* Show video button whenever there's a Daily room (past meeting) */}
              {isPastDailyMeeting && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      // Open tab BEFORE async work to avoid popup blocker
                      const win = window.open('', '_blank');
                      try {
                        let url = recordingUrl;

                        // Fallback 1: refresh expired link via daily_recording_id
                        if (!url && dailyRecordingId) {
                          const { data } = await supabase.functions.invoke('get-daily-recording-access-link', {
                            body: { recording_id: dailyRecordingId },
                          });
                          url = data?.download_link;
                        }

                        // Fallback 2: search by room_name via edge function
                        if (!url && event?.daily_room_name) {
                          const { data } = await supabase.functions.invoke('get-daily-recording-access-link', {
                            body: { room_name: event.daily_room_name },
                          });
                          url = data?.download_link;
                        }

                        if (url && win) {
                          win.location.href = url;
                        } else {
                          win?.close();
                          toast.info('Gravação ainda não disponível. Pode levar alguns minutos após a reunião.');
                        }
                      } catch {
                        win?.close();
                        toast.error('Erro ao buscar gravação');
                      }
                    }}
                  >
                    <Video className="w-3.5 h-3.5 mr-1" />
                    Vídeo
                  </Button>
                  {audioUrl && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(audioUrl, '_blank')}
                    >
                      <Play className="w-3.5 h-3.5 mr-1" />
                      Áudio (MP3)
                    </Button>
                  )}
                </>
              )}
              {!isPastDailyMeeting && (
                <Button
                  type="button"
                  size="sm"
                  disabled={joiningRoom}
                  onClick={async () => {
                    const contactId = selectedContactId ?? event.contact_id;
                    setJoiningRoom(true);
                    try {
                      const result = await ensureMeetingForEvent({
                        calendar_event_id: event.id,
                        daily_room_url: event.daily_room_url,
                        daily_room_name: event.daily_room_name,
                        contact_id: contactId,
                        deal_id: event.deal_id,
                        end_at: event.end_at,
                        attendee_ids: event.attendee_ids || [],
                      });
                      if (result.consultant_token) {
                        openDailyCall({
                          roomUrl: result.room_url,
                          token: result.consultant_token,
                          callId: '',
                          meetingId: result.meeting_id,
                          clientJoinUrl: result.client_join_url,
                        });
                        onClose();
                      } else {
                        // No host token persisted — fallback to opening URL
                        console.warn('[EventFormModal] No host token found, opening URL directly');
                        window.open(result.room_url, '_blank');
                      }
                    } catch (err: any) {
                      console.error('[EventFormModal] join failed:', err);
                      toast.error('Erro ao entrar na sala. Tente novamente.');
                    } finally {
                      setJoiningRoom(false);
                    }
                  }}
                >
                  {joiningRoom ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <ExternalLink className="w-3.5 h-3.5 mr-1" />
                  )}
                  Ingressar
                </Button>
              )}
            </div>
          </div>
        )}

        {isCanceled && (
          <div className="flex items-center gap-2 p-3 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive text-sm font-medium">
            <XCircle className="w-4 h-4 shrink-0" />
            Este evento foi cancelado
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-1">
            <Label htmlFor="ev-title">Título *</Label>
            <Input
              id="ev-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título do evento"
              required
              disabled={isReadOnly}
            />
          </div>

          {/* Type — hidden when defaultType is provided (already chosen upstream) */}
          {!defaultType && <div className="space-y-1">
            <Label>Tipo</Label>
            <Select value={type} onValueChange={(v) => {
              const newType = v as typeof type;
              setType(newType);
              if (newType === 'unavailable' && !title.trim()) {
                setTitle('Indisponível');
              }
            }} disabled={isReadOnly}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="meeting">Reunião</SelectItem>
                <SelectItem value="reminder">Lembrete</SelectItem>
                <SelectItem value="other">Outro</SelectItem>
                <SelectItem value="unavailable">Indisponível</SelectItem>
              </SelectContent>
            </Select>
          </div>}

          {/* All day toggle */}
          <div className="flex items-center gap-2">
            <Switch id="all-day" checked={allDay} onCheckedChange={setAllDay} disabled={isReadOnly} />
            <Label htmlFor="all-day">Dia inteiro</Label>
          </div>

          {/* Date/time fields */}
          {!allDay && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="ev-start">Início</Label>
                <Input
                  id="ev-start"
                  type="datetime-local"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                  required
                  disabled={isReadOnly}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ev-end">Fim</Label>
                <Input
                  id="ev-end"
                  type="datetime-local"
                  value={endAt}
                  onChange={(e) => setEndAt(e.target.value)}
                  required
                  disabled={isReadOnly}
                />
              </div>
            </div>
          )}
          {allDay && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="ev-start-date">Data início</Label>
                <Input
                  id="ev-start-date"
                  type="date"
                  value={startAt.substring(0, 10)}
                  onChange={(e) => setStartAt(e.target.value + 'T00:00')}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ev-end-date">Data fim</Label>
                <Input
                  id="ev-end-date"
                  type="date"
                  value={endAt.substring(0, 10)}
                  onChange={(e) => setEndAt(e.target.value + 'T23:59')}
                  required
                />
              </div>
            </div>
          )}

          {type !== 'unavailable' && (
          <div className="space-y-1">
            <Label>Contato <span className="text-muted-foreground font-normal">(opcional)</span></Label>
            {selectedContactId ? (
              <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-muted/40 text-sm">
                <span className="flex-1 truncate">{selectedContactName}</span>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => { setSelectedContactId(null); setSelectedContactName(''); }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ) : (
              <div className="relative">
                <Input
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  placeholder="Buscar contato..."
                  disabled={isReadOnly}
                  autoComplete="off"
                />
                {contactSearching && (
                  <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-muted-foreground" />
                )}
                {contactResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-40 overflow-y-auto">
                    {contactResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                        onClick={() => {
                          setSelectedContactId(c.id);
                          setSelectedContactName(c.name);
                          setContactSearch('');
                          setContactResults([]);
                        }}
                      >
                        <div className="font-medium">{c.name}</div>
                        {c.sub && <div className="text-xs text-muted-foreground">{c.sub}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Description */}
          <div className="space-y-1">
            <Label htmlFor="ev-desc">Descrição</Label>
            <Textarea
              id="ev-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detalhes do evento (opcional)"
              rows={3}
              disabled={isReadOnly}
            />
          </div>

          {/* Attendees */}
          {type !== 'unavailable' && users.length > 0 && (
            <div className="space-y-1">
              <Label>Participantes</Label>
              <div className="max-h-36 overflow-y-auto rounded-md border p-2 space-y-1">
                {users.filter((u, idx, arr) => arr.findIndex((x) => getUserName(x) === getUserName(u)) === idx).map((u) => (
                  <label key={u.id} className={`flex items-center gap-2 text-sm rounded px-1 py-0.5 ${isReadOnly ? 'opacity-60' : 'cursor-pointer hover:bg-accent'}`}>
                    <input
                      type="checkbox"
                      checked={attendeeIds.includes(u.id)}
                      onChange={() => toggleAttendee(u.id)}
                      className="rounded"
                      disabled={isReadOnly}
                    />
                    {getUserName(u)}
                  </label>
                ))}
              </div>
            </div>
          )}

          <DialogFooter className="flex-col gap-2 sm:flex-row">
            {isEditing && !isReadOnly && (
              <div className="flex gap-2 mr-auto">
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={submitting || event?.status === 'canceled'}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" />
                  Cancelar Evento
                </Button>
              </div>
            )}
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              Fechar
            </Button>
            {!isReadOnly && !isCanceled && (
              <Button type="submit" disabled={submitting || !title.trim()}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Salvar
              </Button>
            )}
          </DialogFooter>
        </form>

        {/* Cancel confirmation dialog */}
        <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancelar evento?</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja cancelar "{event?.title}"? O evento permanecerá visível na agenda como cancelado.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={submitting}>Voltar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancelEvent}
                disabled={submitting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Cancelar Evento
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      </DialogContent>
    </Dialog>
  );
}
